// Priority:
//   1. IN_PROGRESS with startDate <= today <= endDate
//   2. PLANNED with startDate > today (nearest future)
//   3. COMPLETED (most recent by endDate)
//   4. null

/**
 * @param {Array<object>} collabs Collaboration rows. Each item:
 *   { status: 'IN_PROGRESS'|'PLANNED'|'COMPLETED'|'CANCELLED'|'AWAITING_START'|string,
 *     startDate?: string|Date|null,
 *     endDate?: string|Date|null }
 * @returns {object|null} the current collab per D-05 priority, or null when no eligible collab
 */
export function pickActiveCollab (collabs) {
  if (!Array.isArray(collabs) || !collabs.length) return null;
  const today = Date.now();

  const activeNow = collabs.find(c =>
    c && c.status === 'IN_PROGRESS'
    && c.startDate && c.endDate
    && new Date(c.startDate).getTime() <= today
    && today <= new Date(c.endDate).getTime()
  );
  if (activeNow) return activeNow;

  const futurePlanned = collabs
    .filter(c => c && c.status === 'PLANNED' && c.startDate && new Date(c.startDate).getTime() > today)
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))[0];
  if (futurePlanned) return futurePlanned;

  const recentCompleted = collabs
    .filter(c => c && c.status === 'COMPLETED' && c.endDate)
    .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))[0];
  return recentCompleted || null;
}

/**
 * Phase 93.7 D-05 — visibility selector for the «previously completed» round
 * surfaced alongside the active card on the retired zakupy/testing cards.
 *
 * Returns the COMPLETED collab with the latest startDate, or null when none.
 * Tolerates undefined / empty input → null.
 *
 * Consumed by Plan 04: `const prev = pickPreviousCompleted(collabs);` →
 * `const visible = [pickActiveCollab(collabs), prev].filter(Boolean).slice(0, 2);`
 *
 * @param {Array<object>} collabs Collaboration rows (same shape as pickActiveCollab).
 * @returns {object|null} the latest-started COMPLETED collab, or null.
 */
export function pickPreviousCompleted (collabs) {
  if (!Array.isArray(collabs) || !collabs.length) return null;
  const completed = collabs
    .filter(c => c && c.status === 'COMPLETED')
    .sort((a, b) => new Date(b.startDate ?? 0) - new Date(a.startDate ?? 0));
  return completed[0] || null;
}
